import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { 
  Search, Clock, Eye, ChevronRight, Filter, X, 
  Calendar, User, Tag, ArrowRight, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Skeleton loader for blog list
const BlogListSkeleton = () => (
  <div className="border-b border-[#e5e5e5] py-6 animate-pulse">
    <div className="flex gap-6">
      <div className="w-48 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
      <div className="flex-1">
        <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
        <div className="h-6 w-3/4 bg-gray-200 rounded mb-3"></div>
        <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
        <div className="h-4 w-2/3 bg-gray-200 rounded mb-4"></div>
        <div className="flex gap-4">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

const Insights = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [selectedCategory, searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blogs/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      params.append('page', searchParams.get('page') || '1');
      params.append('limit', '10');

      const response = await axios.get(`${API}/blogs?${params}`);
      setBlogs(response.data.blogs || []);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages
      });
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
    const params = new URLSearchParams(searchParams);
    if (category && category !== selectedCategory) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSearchQuery('');
    setSearchParams({});
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Insights & Blog"
        description="Expert insights on PR strategy, media coverage, personal branding, and publicity tips for startups and corporates. Stay updated with the latest PR trends."
        keywords="PR insights, media coverage tips, personal branding, PR strategy, startup PR, corporate PR"
        canonical="/insights"
      />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4">
            <BookOpen className="w-4 h-4" />
            Knowledge Hub
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#1a1a1a]">
            PR Insights & Resources
          </h1>
          <p className="font-manrope text-lg text-[#737373] max-w-2xl mx-auto">
            Expert advice on PR strategy, media coverage, and building your brand's credibility
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-24 py-4 border border-[#e5e5e5] bg-[#fafafa] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] focus:outline-none"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm hover:bg-[#B8960F] transition-colors"
            >
              Search
            </button>
          </div>
        </form>

        {/* Category Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => handleCategoryFilter('')}
            className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
              !selectedCategory 
                ? 'bg-[#D4AF37] text-[#0d0d0d]' 
                : 'bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5]'
            }`}
          >
            All Topics
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategoryFilter(cat)}
              className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                selectedCategory === cat 
                  ? 'bg-[#D4AF37] text-[#0d0d0d]' 
                  : 'bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Active Filters */}
        {(selectedCategory || searchParams.get('search')) && (
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="font-manrope text-sm text-[#737373]">Filters:</span>
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#fee2e2] text-[#D4AF37] rounded-full text-sm">
                {selectedCategory}
                <button onClick={() => handleCategoryFilter('')}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {searchParams.get('search') && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#e5e5e5] text-[#1a1a1a] rounded-full text-sm">
                "{searchParams.get('search')}"
                <button onClick={() => {
                  setSearchQuery('');
                  const params = new URLSearchParams(searchParams);
                  params.delete('search');
                  setSearchParams(params);
                }}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button 
              onClick={clearFilters}
              className="text-[#D4AF37] text-sm hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6">
          <p className="font-manrope text-sm text-[#737373]">
            {loading ? 'Loading...' : `${pagination.total} article${pagination.total !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Blog List */}
        <div className="space-y-0">
          {loading ? (
            [...Array(5)].map((_, i) => <BlogListSkeleton key={i} />)
          ) : blogs.length === 0 ? (
            <div className="text-center py-16 bg-[#fafafa] rounded-lg">
              <BookOpen className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
              <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Articles Found</h3>
              <p className="font-manrope text-[#737373] mb-6">
                Try adjusting your search or filters
              </p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm hover:bg-[#B8960F] transition-colors"
              >
                View All Articles
              </button>
            </div>
          ) : (
            blogs.map((blog, index) => (
              <article 
                key={blog.id}
                className={`py-8 ${index !== blogs.length - 1 ? 'border-b border-[#e5e5e5]' : ''}`}
              >
                <Link to={`/insights/${blog.slug}`} className="group">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Thumbnail */}
                    <div className="w-full md:w-56 h-40 bg-[#f5f5f5] rounded-lg overflow-hidden flex-shrink-0">
                      {blog.featured_image ? (
                        <img 
                          src={blog.featured_image.startsWith('/') ? `${BACKEND_URL}${blog.featured_image}` : blog.featured_image}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#B8960F]">
                          <BookOpen className="w-12 h-12 text-white/50" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {/* Category */}
                      <span className="inline-block px-3 py-1 bg-[#f5f5f5] text-[#D4AF37] rounded-full font-manrope text-xs uppercase tracking-wide mb-3">
                        {blog.category}
                      </span>

                      {/* Title */}
                      <h2 className="font-playfair text-xl md:text-2xl font-bold text-[#1a1a1a] mb-3 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                        {blog.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="font-manrope text-[#737373] mb-4 line-clamp-2">
                        {blog.excerpt}
                      </p>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#a3a3a3]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(blog.published_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {blog.read_time} min read
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {blog.views} views
                        </span>
                      </div>

                      {/* Tags */}
                      {blog.tags && blog.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {blog.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 text-xs text-[#737373]">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="hidden md:flex items-center">
                      <ArrowRight className="w-6 h-6 text-[#e5e5e5] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              </article>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {[...Array(pagination.totalPages)].map((_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', page.toString());
                    setSearchParams(params);
                  }}
                  className={`w-10 h-10 rounded-lg font-manrope text-sm transition-all ${
                    pagination.page === page
                      ? 'bg-[#D4AF37] text-[#0d0d0d]'
                      : 'bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5]'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default Insights;
